<template>
  <modal
    name="acceptBookingRequestModal"
    transition="pop-out"
    class="accept-booking-request-modal"
    :width="600"
    :focus-trap="false"
    :clickToClose="false"
    height="auto"
    v-model="show"
  >
    <div class="position-relative modal-outer-container">
      <div class="modal-header border-bottom">
        <div class="ml-3 pl-2">Please fill in the form below to proceed</div>
        <button class="btn border-0" @click="closeModal">
          <i class="fas fa-times fa-lg"></i>
        </button>
      </div>
      <form @submit.prevent="acceptBookingRequestHelper">
        <div class="col-12 modal-body">
          <div class="form-group" v-if="canChooseProvider || selectedOverlap">
            <label v-if="canChooseProvider"
              >{{ $filters.providerLabel("singular", true) }}/Admin</label
            >
            <multiselect
              v-if="canChooseProvider"
              v-model="selectedProvider"
              class="app-select no-labels"
              :options="dropdownProviders"
              :preserve-search="true"
              :allow-empty="false"
              :show-labels="false"
              :placeholder="`Search for ${$providerLabel({
                type: 'singular',
                capitalize: true,
              })}/Admin`"
              label="name"
              :loading="providersLoading"
            >
              <template #option="props">
                <div class="d-flex flex-column">
                  <div class="font-weight-bold select-label">
                    {{ props.option.name }}
                  </div>
                  <div class="small email-container" style="opacity: 0.9">
                    ({{ props.option.email }})
                  </div>
                  <div
                    v-if="props.option.overlapCount"
                    class="small text-dark-red mt-1"
                  >
                    <i class="fas fa-exclamation-triangle mr-1"></i>
                    {{ overlapMarker(props.option.overlapCount) }}
                  </div>
                </div>
              </template>
              <template #noResult>
                <div class="no-result">
                  Oops! No {{ $filters.providerLabel("plural") }} found.
                  Consider changing the search query.
                </div>
              </template>
            </multiselect>
            <div v-if="selectedOverlap" :class="{ 'mt-2': canChooseProvider }">
              <div class="small text-dark-red">
                <div>
                  <i class="fas fa-exclamation-triangle mr-1"></i>
                  {{ overlapHeading }}
                </div>
                <div
                  v-for="(session, index) in shownSessions"
                  :key="index"
                  class="pl-4 text-truncate"
                >
                  {{ sessionLine(session) }}
                </div>
                <div v-if="moreSessions" class="pl-4">
                  + {{ moreSessions }} more
                </div>
              </div>
              <div class="custom-control custom-checkbox mt-2">
                <input
                  type="checkbox"
                  v-model="allowOverlap"
                  id="acceptBookingAllowOverlap"
                  class="custom-control-input"
                />
                <label
                  class="custom-control-label text-dark-red"
                  for="acceptBookingAllowOverlap"
                >
                  Allow Overlapping With Other Sessions
                </label>
              </div>
            </div>
          </div>
          <input-control
            control="number"
            v-model="providerRate"
            type="vertical"
            step="0.1"
            min="0"
          >
            {{ $filters.providerLabel("singular", true) }} Rate
          </input-control>
          <div class="form-group">
            <label>
              Memo
              <i
                class="fa fa-info-circle ml-2 pointer-cursor"
                v-tooltip.top="'Seen in billing & payroll reports'"
              ></i>
            </label>
            <textarea
              class="form-control"
              v-model.trim="memo"
              rows="3"
            ></textarea>
          </div>
          <div class="form-group">
            <label
              >Reason - seen by
              {{ $filters.customerLabel("singular", false) }}</label
            >
            <textarea
              class="form-control"
              v-model.trim="reason"
              rows="3"
            ></textarea>
          </div>
          <input-control
            control="number"
            v-model="expenses"
            type="vertical"
            step="0.01"
          >
            Expenses $
          </input-control>
          <div class="form-group">
            <label> Billing Details</label>
            <textarea
              class="form-control"
              v-model.trim="billingDetails"
              rows="3"
            ></textarea>
          </div>
        </div>
        <div
          class="
            d-flex
            justify-content-center
            border-top
            align-items-center
            py-3
          "
        >
          <button class="btn btn-danger mr-3" @click.prevent="cancel">
            Cancel
          </button>
          <save
            classes="btn btn-black"
            :saving="acceptBookingRequestLoading == bookingRequest.id"
            type="submit"
          >
            Accept Request
          </save>
        </div>
      </form>
    </div>
  </modal>
</template>
<script>
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import Swal from "sweetalert2";
import Multiselect from "vue-multiselect";
import { mapActions, mapState } from "vuex";

dayjs.extend(utc);

const SESSIONS_SHOWN = 3;

export default {
  components: {
    Multiselect,
  },
  props: {
    bookingRequest: {
      type: Object,
      required: true,
    },
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "finish"],
  data() {
    return {
      selectedProvider: null,
      providerRate: 0,
      memo: "",
      reason: "",
      expenses: null,
      billingDetails: "",
      allowOverlap: false,
      providers: [],
      providersLoading: false,
    };
  },
  updated() {
    this.calculateProviderRate();
  },
  watch: {
    selectedProvider() {
      this.allowOverlap = false;
      this.calculateProviderRate();
    },
    modelValue: {
      immediate: true,
      async handler(open) {
        if (open) {
          await this.loadProviders();
        }
      },
    },
  },
  computed: {
    ...mapState({
      loggedInUser: (state) => state.auth.user,
      acceptBookingRequestLoading: (state) =>
        state.booking.acceptBookingRequestLoading,
    }),
    canChooseProvider() {
      return !!(
        this.loggedInUser &&
        (this.loggedInUser.isAdmin || this.loggedInUser.isManagingAdmin)
      );
    },
    dropdownProviders() {
      return this.providers
        .filter((provider) => provider.status !== "INACTIVE")
        .map((provider) => ({
          id: provider.id,
          name: provider.name,
          email: provider.email,
          overlapCount: provider.overlapping_sessions?.length || 0,
        }));
    },
    selectedRow() {
      return this.selectedProvider
        ? this.providers.find(
            (provider) => provider.id == this.selectedProvider.id
          )
        : null;
    },
    /** The selected person's sessions in the requested slot, or null when they have none. */
    selectedOverlap() {
      const sessions = this.selectedRow?.overlapping_sessions;
      return sessions?.length ? sessions : null;
    },
    shownSessions() {
      return this.selectedOverlap.slice(0, SESSIONS_SHOWN);
    },
    moreSessions() {
      return this.selectedOverlap.length - this.shownSessions.length;
    },
    overlapHeading() {
      const count = this.selectedOverlap.length;
      return count === 1
        ? `${this.selectedName} already has a session in this slot:`
        : `${this.selectedName} already has ${count} sessions in this slot:`;
    },
    selectedName() {
      const row = this.selectedRow;
      return row ? `${row.first} ${row.last}` : this.selectedProvider?.name;
    },
    show: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
  },
  methods: {
    ...mapActions({
      getBookingRequestProviders: "booking/getBookingRequestProviders",
      acceptBookingRequest: "booking/acceptBookingRequest",
    }),
    async loadProviders() {
      if (!this.canChooseProvider && !this.loggedInUser?.isProvider) {
        return;
      }
      this.providersLoading = true;
      this.providers = await this.getBookingRequestProviders(
        this.bookingRequest.id
      );
      this.providersLoading = false;
      if (!this.canChooseProvider) {
        this.selectedProvider = {
          id: this.loggedInUser.id,
          name: this.loggedInUser.name,
          email: this.loggedInUser.email,
        };
      }
      this.$forceUpdate();
    },
    overlapMarker(count) {
      return count === 1
        ? "Has a session scheduled in this requested slot"
        : `Has ${count} sessions scheduled in this requested slot`;
    },
    sessionLine(session) {
      const start = dayjs(session.start_time).utc();
      const end = dayjs(session.end_time).utc();
      const endFormat = end.isSame(start, "day") ? "h:mm A" : "MMM D, h:mm A";
      const time = `${start.format("MMM D, h:mm A")} – ${end.format(
        endFormat
      )}`;
      const who =
        session.type === "group_session"
          ? `Group: ${session.program} (${
              session.participants
            } ${this.$filters.customerLabel(
              session.participants === 1 ? "singular" : "plural"
            )})`
          : session.with;
      return who ? `${time} · ${who}` : time;
    },
    acceptBookingRequestHelper: function () {
      if (!this.selectedProvider) {
        Swal.fire({
          icon: "error",
          text: `Please select a ${this.$providerLabel({
            type: "singular",
            capitalize: false,
          })}/admin to proceed`,
        });
        return;
      }
      if (this.selectedOverlap && !this.allowOverlap) {
        Swal.fire({
          icon: "error",
          text: `${this.selectedName} already has a session in this slot. Tick Allow Overlapping With Other Sessions to accept anyway.`,
        });
        return;
      }

      const payload = {
        bookingRequestId: this.bookingRequest.id,
        provider_id: this.selectedProvider.id,
        provider_rate: this.providerRate,
        memo: this.memo,
        reason: this.reason,
        expenses: this.expenses,
        billing_details: this.billingDetails,
        allow_overlap: this.allowOverlap,
      };
      if (!this.providerRate || parseFloat(this.providerRate) == 0) {
        const vm = this;
        const swalWithBootstrapButtons = Swal.mixin({
          customClass: {
            confirmButton: "btn btn-black ml-4",
            cancelButton: "btn btn-danger",
          },
          buttonsStyling: false,
        });

        return swalWithBootstrapButtons
          .fire({
            title: "Are you sure?",
            text: `${this.$providerLabel({
              type: "singular",
              capitalize: true,
            })} Rate is zero or empty, are you sure you want to proceed?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, sure!",
            cancelButtonText: "Cancel",
            reverseButtons: true,
          })
          .then((result) => {
            if (result.isConfirmed) {
              vm.acceptBookingRequest(payload).then((success) => {
                if (success) {
                  Swal.fire({
                    title: "Success",
                    text: "Booking request has been accepted successfully",
                    icon: "success",
                  });
                  vm.$emit("finish");
                  vm.resetFormData();
                  vm.closeModal();
                }
              });
            }
          });
      } else {
        this.acceptBookingRequest(payload).then((success) => {
          if (success) {
            Swal.fire({
              title: "Success",
              text: "Booking request has been accepted successfully",
              icon: "success",
            });
            this.$emit("finish");
            this.resetFormData();
            this.closeModal();
          }
        });
      }
    },
    resetFormData: function () {
      if (
        this.loggedInUser &&
        (this.loggedInUser.isAdmin || this.loggedInUser.isManagingAdmin)
      ) {
        this.selectedProvider = null;
      }
      this.providerRate = 0;
      this.memo = "";
      this.reason = "";
      this.expenses = null;
      this.billingDetails = "";
      this.allowOverlap = false;
    },
    cancel: function () {
      this.resetFormData();
      this.closeModal();
    },
    calculateProviderRate: function () {
      if (this.selectedProvider) {
        const providerId = this.selectedProvider?.id;
        const service = this.bookingRequest.treatment?.service;
        if (providerId && service) {
          let selectedProvider = this.providers.find(
            (user) => user.id == providerId
          );
          const providerProfession = (selectedProvider.professions || []).find(
            (profession) => profession.id == service.profession_id
          );
          if (providerProfession) {
            this.providerRate = providerProfession.rate;
          } else {
            this.providerRate = 0;
          }
        } else {
          this.providerRate = 0;
        }
      }
    },
    closeModal() {
      this.$emit("update:modelValue", false);
    },
  },
};
</script>
<style lang="scss" scoped>
.accept-booking-request-modal {
  .ccc-modal-content {
    background-color: #ffffff;
  }
  .modal-body {
    max-height: 500px;
    overflow-y: auto;
  }
}
</style>
